import { Link, useNavigate } from '@tanstack/react-router'
import { useMemo } from 'react'
import { FiHelpCircle } from 'react-icons/fi'
import { SlLogout, SlSettings, SlUser } from 'react-icons/sl'
import { useSelector } from 'react-redux'
import Dropdown from '@/components/Dropdown'
import { Button } from '@/components/orders/Button'
import useSignOutAndClearStore from '@/hooks/useSignOutAndClearStore'
import { useGetUserSelf } from '@/hooks/v1'
import type { IRootState } from '@/store'
import { getApiKey } from '@/utils/apiKeyAuth'
import { copyToken } from '@/utils/tokenUtils'

export const ProfileMenu = () => {
  const signOutAndClearStore = useSignOutAndClearStore()
  const navigate = useNavigate()
  const { data: userSelf } = useGetUserSelf()
  const user = useMemo(() => userSelf?.data, [userSelf])
  const isRtl = useSelector((state: IRootState) => state.themeConfig.rtlClass) === 'rtl'
  const handleSignOut = async () => {
    const hadApiKey = Boolean(getApiKey())
    try {
      await signOutAndClearStore()
    } finally {
      await navigate({
        to: hadApiKey ? '/auth/session-closed' : '/auth/sign-in',
        replace: true,
      })
    }
  }

  return (
    <>
      <div className="dropdown shrink-0 flex items-center">
        <Dropdown
          offset={[0, 8]}
          placement={isRtl ? 'bottom-start' : 'bottom-end'}
          btnClassName="relative group block items-center h-full"
          button={
            <div className="relative">
              <svg
                className="w-7 h-7 rounded-full border-primary border self-center saturate-50 group-hover:saturate-100 items-center p-1 bg-gray-100 dark:bg-gray-700"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
              </svg>
            </div>
          }
        >
          <ul className="!py-0 w-[250px] font-semibold !text-[color:var(--color-text-1)]">
            <li>
              <div className="flex items-center px-4 py-4">
                <svg
                  className="rounded-full w-11 h-11 p-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                </svg>
                <div className="ltr:pl-2 rtl:pr-2 truncate">
                  <h4 className="text-base text-[color:var(--color-text-2)]">
                    {user?.first_name ?? 'first name'}
                    {user?.last_name ?? 'last name'}
                    <span className="text-xs bg-success-light rounded text-success px-1 ltr:ml-2 rtl:ml-2">
                      Pro
                    </span>
                  </h4>
                  <div className="text-xs text-[color:var(--color-text-0)] hover:text-primary">
                    {user?.email?.slice(0, 25)}...
                  </div>
                </div>
              </div>
            </li>
            <li>
              <Link
                to="/user/profile"
                className="text-[color:var(--color-text-1)] hover:text-primary"
              >
                <SlUser size={24} className="mr-2 text-[color:var(--color-text-0)]" />
                Profile
              </Link>
            </li>
            <li>
              <Link
                to="/user/settings"
                className="text-[color:var(--color-text-1)] hover:text-primary"
              >
                <SlSettings size={24} className="mr-2 text-[color:var(--color-text-0)]" />
                Account Settings
              </Link>
            </li>
            <li>
              <Link
                to="/user/affiliates"
                className="text-[color:var(--color-text-1)] hover:text-primary"
              >
                <SlUser size={24} className="mr-2" />
                Affiliates panel
              </Link>
            </li>
            <li>
              <Link to="/help" className="text-[color:var(--color-text-1)] hover:text-primary">
                <FiHelpCircle size={24} className="mr-2 text-[color:var(--color-text-0)]" />
                Help
              </Link>
            </li>
            <li className="border-t border-white-light dark:border-white-light/10">
              <button type="button" className="text-danger !py-3" onClick={handleSignOut}>
                <SlLogout size={24} className="pr-0.5 mr-2 text-danger" />
                Sign Out
              </button>
            </li>
            <li className={'p-2'}>
              <Button
                className="w-full uppercase shadow-[0_10px_20px_-10px_rgba(67,97,238,0.44)] text-nowrap"
                noOpacityColors
                onClick={copyToken}
              >
                Copy token
              </Button>
            </li>
          </ul>
        </Dropdown>
      </div>
    </>
  )
}
